import React, { useState, useEffect } from 'react';
import { useHistory } from "react-router";
import RadioButton from 'components/RadioButton/RadioButton';
import { Container, Paragraph, ButtonContainer, WarningBox, AccountBox, BoxTitle, WarningIcon, DeleteStyledButton } from '../DeleteAccount/DeleteAccount.styles';
import { OptionBox, OptionBoxText, OptionSelection } from './DeleteAccountSecondStep.styles';
import DeleteModal from './components/DeleteModal';
import useActionApi from "../../../hooks/useActionApi";
import Api from "../../../api";
import FixedLoader from "../../../components/FixedLoader/FixedLoader";
import FixedError from "../../../components/FixedError/FixedError";
import { TopBarRedesigned } from "components/TopBarRedesigned";
import TextField from '@mui/material/TextField';
import { SUPPORT_EMAIL } from "utils/constants";
import clsx from 'clsx';
// @ts-ignore
const TempStars = window.TempStars

declare let window: any
declare let cordova: any

const HygienistDeleteAccountSecondStep: React.FC = () => {
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [details, setDetails] = useState<string | null>(null);

  const OPTIONS = {
    "MISTAKE": "0",
    "NOT_READY": "1",
    "NOT_UNDERSTANDING": "2",
    "OTHER": "3"
  }

  const [deleteUserLoading, deleteUserError, deleteUserAccount] = useActionApi({
    apiMethod: Api.deleteUser,
    onSuccess: (response) => {},
  });

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleDeleteAccount = async() => {
    const deleteReason = selected === OPTIONS.MISTAKE ? "I made a mistake during sign-up" : selected === OPTIONS.NOT_READY ? "I'm not ready to use TempStars yet" : selected === OPTIONS.NOT_UNDERSTANDING ? "I don't understand how to use TempStars" : "Other"
    const data = {
      deleteReason,
      details: details || null
    }
    await deleteUserAccount(data)
    .then(()=>{
      TempStars.User.logout();
      history.push('landing');
    })
    .catch((error: any) => {
      console.error("Error deleting user account:", error);
    });
  }

  const helpEmail = SUPPORT_EMAIL.US;

  const handleContactSupport = () => {
    if (window.cordova && window.cordova.platformId === 'ios') {
      cordova.plugins.email.open({
          to:      helpEmail,
          subject: '',
          body:    ''
      });
  } else {
      window.open(`mailto:${helpEmail}`, '_system');
  }
  };

  return (
    <Container>
      <TopBarRedesigned
        title={'Delete your account?'}
        onBack={() => {
          history.goBack();
        }}
      />

      <WarningBox>
        <BoxTitle $titleColor="var(--primary-main)"><WarningIcon $iconColor="var(--primary-main)" /> Critical Warning:</BoxTitle>
        <Paragraph>
          Are you sure you want to delete your account? This action cannot be undone.
        </Paragraph>
        <ul>
          <li>You will lose access to all your data and past records</li>
          <li>You won't be able to log in or use any TempStars services</li>
          <li>Your personal information will be removed from our active systems</li>
        </ul>
      </WarningBox>

      <BoxTitle $titleColor="var(--labels-primary, #000)">Please select a reason for deleting your account:</BoxTitle>
      <OptionBox>
        <OptionSelection>
        <RadioButton 
            isActive={selected === OPTIONS.MISTAKE}
            handleClick={() => setSelected(OPTIONS.MISTAKE)}
            item={OPTIONS.MISTAKE}
        />
        <div>
            <OptionBoxText $fontWeight="700">I made a mistake during sign-up</OptionBoxText>
            <OptionBoxText>You don't need to delete your account to correct sign-up mistakes. Our support team can help you update your information and get you back on track.</OptionBoxText>
        </div>
        </OptionSelection>
        {selected === OPTIONS.MISTAKE && (
            <TextField
            id="outlined-multiline-flexible"
            multiline
            minRows={4}
            maxRows={6}
            fullWidth
            placeholder="Please share more about your concerns. This will help us improve our service."
            onChange={(e) => setDetails(e.target.value)}
            sx={{
              '& .MuiInputBase-input': {
           padding: '0 0 3rem !important'
              }
            }}
            />
        )}
      </OptionBox>
      <OptionBox>
      <OptionSelection>
      <RadioButton 
            isActive={selected === OPTIONS.NOT_READY}
            handleClick={() => setSelected(OPTIONS.NOT_READY)}
            item={OPTIONS.NOT_READY}
        />
        <div>
            <OptionBoxText $fontWeight="700">I'm not ready to use TempStars yet</OptionBoxText>
            <OptionBoxText>Instead of deleting your account, you can keep it inactive until you're ready to start using TempStars – we're here when you're ready!</OptionBoxText>
        </div>
        </OptionSelection>
        {selected === OPTIONS.NOT_READY && (
            <TextField
            id="outlined-multiline-flexible"
            multiline
            minRows={4}
            maxRows={6}
            fullWidth
            placeholder="Please share more about your concerns. This will help us improve our service."
            onChange={(e) => setDetails(e.target.value)}
            sx={{
              '& .MuiInputBase-input': {
                padding: '0 0 3rem !important'
              }
            }}
            />
        )}
      </OptionBox>
      <OptionBox>
      <OptionSelection>
      <RadioButton 
            isActive={selected === OPTIONS.NOT_UNDERSTANDING}
            handleClick={() => setSelected(OPTIONS.NOT_UNDERSTANDING)}
            item={OPTIONS.NOT_UNDERSTANDING}
        />
        <div>
            <OptionBoxText $fontWeight="700">I don't understand how to use TempStars</OptionBoxText>
            <OptionBoxText>We'd be happy to guide you through using TempStars. Our support team can provide a personalized walkthrough.</OptionBoxText>
        </div>
        </OptionSelection>
        {selected === OPTIONS.NOT_UNDERSTANDING && (
            <TextField
            id="outlined-multiline-flexible"
            multiline
            minRows={4}
            maxRows={6}
            fullWidth
            placeholder="Please share more about your concerns. This will help us improve our service."
            onChange={(e) => setDetails(e.target.value)}
            sx={{
              '& .MuiInputBase-input': {
                padding: '0 0 3rem !important'
              }
            }}
            />
        )}
      </OptionBox>
      <OptionBox>
      <OptionSelection>
      <RadioButton 
            isActive={selected === OPTIONS.OTHER}
            handleClick={() => setSelected(OPTIONS.OTHER)}
            item={OPTIONS.OTHER}
        />
        <div>
            <OptionBoxText $fontWeight="700">Other</OptionBoxText>
            <OptionBoxText>Before deleting your account, please consider reaching out to our support team. We should be able to address any of your concerns.</OptionBoxText>
        </div>
        </OptionSelection>
        {selected === OPTIONS.OTHER && (
            <TextField
            id="outlined-multiline-flexible"
            multiline
            minRows={4}
            maxRows={6}
            fullWidth
            placeholder="Please share more about your concerns. This will help us improve our service."
            onChange={(e) => setDetails(e.target.value)}
            sx={{
              '& .MuiInputBase-input': {
                padding: '0 0 3rem !important'
              }
            }}
            />
        )}
      </OptionBox>

      <AccountBox $backgroundColor="var(--background-success)" $borderColor="var(--info-main)">
          <BoxTitle $titleColor="var(--info-main)">Alternatives to consider</BoxTitle>
          <ul>
            <li>Contact support to update your account information</li>
            <li>Keep your account inactive until you're ready to use TempStars</li>
            <li>Request a personalized walkthrough of the platform</li>
        </ul>
      </AccountBox>
      <ButtonContainer>
        <DeleteStyledButton
          onClick={handleContactSupport}
          variant="outlined"
          color="primary"
          size="large"
          className={clsx("deleteButton", "no-fastclick")}
        >
          Contact Support
        </DeleteStyledButton>
        <DeleteStyledButton
          onClick={()=>setIsOpen(true)}
          variant="contained"
          color="secondary"
          size="large"
          className={clsx("deleteButton", "no-fastclick")}
        >
          Continue to Delete
        </DeleteStyledButton>
      </ButtonContainer>
      <DeleteModal isOpen={isOpen} onClose={handleClose} onConfirm={handleDeleteAccount} loading={deleteUserLoading} />
      <FixedLoader loading={deleteUserLoading} />
      <FixedError error={deleteUserError} />
    </Container>
  );
};

export default HygienistDeleteAccountSecondStep;